import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import DateField from "components/CustomFields/DateField";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";
import CustomMUIDataTable from "components/MUIDatatable/CustomMUIDataTable";
import PageTitle from "components/PageTitle/PageTitle";
import { format } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import { getApiErrorMessage } from "utils/apiErrors";
import { getApiRequestAttempts } from "utils/apiRequestAttempts";
import { getCurrentDate } from "utils/dateGetters";
import { dbGet, dbPost } from "utils/DBFetchers";

function AttemptResponse({ title, attempt }) {
  return (
    <Paper variant="outlined" style={{ padding: "16px", marginBottom: "16px", backgroundColor: "#f9f9f9" }}>
      <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>{title}</Typography>
      <Typography variant="body2" style={{ marginBottom: "8px" }}>
        Status HTTP: {attempt.status_code}
      </Typography>
      <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all", fontSize: "0.875rem" }}>
        {attempt.response == null
          ? "Sin contenido de respuesta."
          : JSON.stringify(attempt.response, null, 2)}
      </pre>
    </Paper>
  );
}

export default function ApiRequestsPage() {
  const [apiRequests, setApiRequests] = useState([]);
  const [apiUsers, setApiUsers] = useState([]);

  const [selectedClient, setSelectedClient] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedOpCode, setSelectedOpCode] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [hasListError, setHasListError] = useState(false);
  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [modalData, setModalData] = useState(null);
  const mounted = useRef(true);
  const listLoadVersion = useRef(0);
  const detailLoadVersion = useRef(0);
  const activeRequestId = useRef(null);
  const retryInFlight = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      listLoadVersion.current += 1;
      detailLoadVersion.current += 1;
    };
  }, []);

  useEffect(() => {
    dbGet("wallet/api_users")
      .then((data) => {
        if (mounted.current) setApiUsers(data);
      })
      .catch(() => {
        if (mounted.current) {
          snackbarOpen("error", "No se pudieron cargar los clientes API.");
        }
      });
  }, []);

  // Recibe un objeto opcional para forzar filtros específicos (ej. al limpiar)
  const fetchApiRequests = async (overrideFilters) => {
    const version = ++listLoadVersion.current;
    setIsLoading(true);
    setHasListError(false);

    try {
      // Si vienen filtros forzados (override), los usamos; de lo contrario, usamos el estado actual
      const client = overrideFilters !== undefined ? overrideFilters.client : selectedClient;
      const status = overrideFilters !== undefined ? overrideFilters.status : selectedStatus;
      const opCode = overrideFilters !== undefined ? overrideFilters.opCode : selectedOpCode;
      const period = overrideFilters !== undefined ? overrideFilters.period : selectedPeriod;

      const params = new URLSearchParams();
      if (client) params.append("api_user_id", client);
      if (status) params.append("status", status);
      if (opCode) params.append("op_code", opCode);

      if (period) {
        params.append("period", format(period, "yyyyMM"));
      }

      const endpoint = `wallet/api_requests?${params.toString()}`;

      const data = await dbGet(endpoint);
      if (!mounted.current || version !== listLoadVersion.current) return;
      if (!Array.isArray(data)) throw new Error("La respuesta del listado de peticiones no es válida.");
      setApiRequests(data);
    } catch (error) {
      if (!mounted.current || version !== listLoadVersion.current) return;
      setHasListError(true);
      snackbarOpen("error", getApiErrorMessage(error, "Error al cargar API requests."));
    } finally {
      if (mounted.current && version === listLoadVersion.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchApiRequests();
  }, []);

  const handleClearFilters = () => {
    setSelectedClient("");
    setSelectedStatus("");
    setSelectedOpCode("");
    setSelectedPeriod(null);

    fetchApiRequests({ client: "", status: "", opCode: "", period: null });
  };

  const loadDetail = async (requestId) => {
    const version = ++detailLoadVersion.current;
    setIsModalLoading(true);
    setModalData(null);

    try {
      const data = await dbGet(`wallet/api_requests/${requestId}`);
      if (!mounted.current || version !== detailLoadVersion.current) return;
      if (
        data?.id !== requestId ||
        !Number.isInteger(data.http_response_status_code) ||
        data.http_response_status_code < 100 ||
        data.http_response_status_code > 599
      ) {
        throw new Error("La respuesta del detalle de la petición no es válida.");
      }
      setModalData(data);
    } catch (error) {
      if (!mounted.current || version !== detailLoadVersion.current) return;
      snackbarOpen("error", getApiErrorMessage(error, "Error al cargar el detalle de la petición."));
    } finally {
      if (mounted.current && version === detailLoadVersion.current) {
        setIsModalLoading(false);
      }
    }
  };

  const handleOpenDetail = (requestId) => {
    if (retryInFlight.current) return;
    activeRequestId.current = requestId;
    setIsModalOpen(true);
    loadDetail(requestId);
  };

  const handleCloseModal = () => {
    if (retryInFlight.current) return;
    detailLoadVersion.current += 1;
    activeRequestId.current = null;
    setIsModalOpen(false);
    setIsModalLoading(false);
    setModalData(null);
  };

  const handleReloadData = () => {
    if (retryInFlight.current || isModalLoading || isLoading || !activeRequestId.current) return;
    loadDetail(activeRequestId.current);
    fetchApiRequests();
  };

  const handleRetryRequest = async () => {
    if (
      retryInFlight.current || isModalLoading || isLoading || hasListError ||
      !isModalOpen || !modalData?.id || modalData.id !== activeRequestId.current ||
      modalData.http_response_status_code < 400
    ) return;

    const requestId = modalData.id;
    retryInFlight.current = true;
    setIsRetrying(true);

    try {
      await dbPost(`wallet/api_requests/${requestId}/retry`, {});
      if (mounted.current) snackbarOpen("success", "Petición reintentada exitosamente.");
    } catch (error) {
      if (mounted.current) {
        snackbarOpen("error", getApiErrorMessage(error, "Ocurrió un error al reintentar la petición."));
      }
    } finally {
      try {
        if (mounted.current) {
          await Promise.all([loadDetail(requestId), fetchApiRequests()]);
        }
      } finally {
        retryInFlight.current = false;
        if (mounted.current) setIsRetrying(false);
      }
    }
  };

  const { originalAttempt, retryAttempts } = getApiRequestAttempts(modalData);
  const latestRetry = retryAttempts[retryAttempts.length - 1];

  const columns = [
    {
      name: "__detail__",
      label: " ",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const rowId = tableMeta.rowData[1];
          return (
            <div style={{ display: "flex", gap: 0 }}>
              <Tooltip title="Ver detalle">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOpenDetail(rowId);
                  }}
                  aria-label="Ver detalle"
                >
                  <ContentPasteSearchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
        setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", width: 60 } }),
        setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
      }
    },
    {
      name: "id",
      label: "Nro. de Operación",
      options: { filter: false, sort: true }
    },
    {
      name: "created_at",
      label: "Fecha",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
          return value ? format(new Date(value), "dd-MM-yyyy HH:mm:ss") : "-";
        }
      }
    },
    {
      name: "op_code",
      label: "Operación",
      options: { filter: true, sort: true }
    },
    {
      name: "http_response_status_code",
      label: "Status HTTP",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const color = value >= 400 ? "red" : "green";
          return <strong style={{ color }}>{value}</strong>;
        }
      }
    }
  ];

  return (
    <>
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />

      <PageTitle title="Auditoría de Peticiones API" />

      <Grid container spacing={4}>
        {/* Contenedor de Filtros */}
        <Grid item xs={12}>
          <Paper style={{ padding: "24px" }}>
            <Typography variant="h6" style={{ fontWeight: "bold", marginBottom: "16px" }}>
              Filtros de Búsqueda
            </Typography>
            <Divider style={{ marginBottom: "24px" }} />

            <Grid container justifyContent="center">
              <Grid item xs={12} md={10} lg={8}>
                <Grid container spacing={3}>
                  {/* Fila 1 */}
                  <Grid item container xs={12} md={6} alignItems="center">
                    <Grid item xs={4} style={{ textAlign: "right", paddingRight: "16px" }}>
                      <Typography>Cliente</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        select
                        fullWidth
                        value={selectedClient}
                        onChange={(e) => setSelectedClient(e.target.value)}
                        variant="outlined"
                        size="small"
                      >
                        <MenuItem value=""><em>Seleccione</em></MenuItem>
                        {apiUsers.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>

                  <Grid item container xs={12} md={6} alignItems="center">
                    <Grid item xs={4} style={{ textAlign: "right", paddingRight: "16px" }}>
                      <Typography>Estado</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        select
                        fullWidth
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        variant="outlined"
                        size="small"
                      >
                        <MenuItem value=""><em>Seleccione</em></MenuItem>
                        <MenuItem value="accepted">Aceptadas</MenuItem>
                        <MenuItem value="rejected">Rechazadas</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>

                  {/* Fila 2 */}
                  <Grid item container xs={12} md={6} alignItems="center">
                    <Grid item xs={4} style={{ textAlign: "right", paddingRight: "16px" }}>
                      <Typography>Operación</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        select
                        fullWidth
                        value={selectedOpCode}
                        onChange={(e) => setSelectedOpCode(e.target.value)}
                        variant="outlined"
                        size="small"
                      >
                        <MenuItem value=""><em>Seleccione</em></MenuItem>
                        <MenuItem value="POST_WALLET_SALES">Venta (normal)</MenuItem>
                        <MenuItem value="POST_WALLET_NO_COUPON_SALES">Venta (sin cupón)</MenuItem>
                        <MenuItem value="DELETE_WALLET_SALES">Cancelación</MenuItem>
                        <MenuItem value="POST_WALLET_DISTRIBUTOR_TRANSACTIONS">Nota de crédito</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>

                  <Grid item container xs={12} md={6} alignItems="center">
                    <Grid item xs={4} style={{ textAlign: "right", paddingRight: "16px" }}>
                      <Typography>Periodo</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <DateField
                        views={["year", "month"]}
                        minDate={new Date("2022-01-01")}
                        maxDate={getCurrentDate()}
                        format={"MM/yyyy"}
                        value={selectedPeriod}
                        onChange={(date) => setSelectedPeriod(date)}
                        inputVariant="outlined"
                        size="small"
                        fullWidth={true}
                      />
                    </Grid>
                  </Grid>

                </Grid>
              </Grid>
            </Grid>

            <Grid container justifyContent="flex-end" style={{ marginTop: "24px", paddingRight: "16px" }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleClearFilters}
                style={{ marginRight: "16px" }}
              >
                Limpiar Filtros
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => fetchApiRequests()}
              >
                Aplicar
              </Button>
            </Grid>
          </Paper>
        </Grid>

        {/* Tabla de Resultados */}
        <Grid item xs={12}>
          {isLoading ? (
            <Paper style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
              <CircularProgress />
            </Paper>
          ) : (
            <CustomMUIDataTable
              title="Registro histórico"
              data={apiRequests}
              columns={columns}
              options={{
                selectableRows: "none",
                responsive: "standard",
                print: false,
                download: true,
                filterType: "multiselect",
                elevation: 1,
              }}
            />
          )}
        </Grid>
      </Grid>

      {/* Modal de Detalle */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle disableTypography>
          <Typography variant="h5" style={{ fontWeight: "bold" }}>
            Detalle de Petición API
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {isModalLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
              <CircularProgress />
            </div>
          ) : modalData ? (
            <Grid container spacing={3}>

              {/* Sección 1: Información General */}
              <Grid item xs={12}>
                <Typography variant="h6" color="textSecondary" style={{ marginBottom: "8px" }}>
                  Información General
                </Typography>
                <Divider style={{ marginBottom: "16px" }} />
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">Nro. Operación</Typography>
                    <Typography variant="body1">{modalData.id}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">Fecha de petición original</Typography>
                    <Typography variant="body1">
                      {modalData.created_at ? format(new Date(modalData.created_at), "dd-MM-yyyy HH:mm:ss") : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">Operación</Typography>
                    <Typography variant="body1">{modalData.op_code_name}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">Status HTTP actual</Typography>
                    <Typography
                      variant="body1"
                      style={{
                        fontWeight: "bold",
                        color: modalData.http_response_status_code >= 400 ? "red" : "green"
                      }}
                    >
                      {modalData.http_response_status_code}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Sección 2: Contenido de la Solicitud (Datos formateados) */}
              <Grid item xs={12}>
                <Typography variant="h6" color="textSecondary" style={{ marginTop: "8px", marginBottom: "8px" }}>
                  Contenido de la Solicitud
                </Typography>
                <Divider style={{ marginBottom: "16px" }} />
                <Grid container spacing={2}>
                  {modalData.request_details && modalData.request_details.length > 0 ? (
                    modalData.request_details.map((detail, idx) => (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                        <Typography variant="body2" color="textSecondary">{detail.label}</Typography>
                        <Typography variant="body1" style={{ wordBreak: "break-word" }}>
                          {typeof detail.value === 'object' ? JSON.stringify(detail.value) : String(detail.value)}
                        </Typography>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        No hay parámetros adicionales en la solicitud.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {/* Sección 3: Reintentos agrupados debajo de su intento original */}
              <Grid item xs={12}>
                <Typography variant="h6" color="textSecondary" style={{ marginTop: "8px", marginBottom: "8px" }}>
                  Historial de la petición
                </Typography>
                <Divider style={{ marginBottom: "16px" }} />
                <Paper variant="outlined" style={{ padding: "16px" }}>
                  <AttemptResponse title="Intento original" attempt={originalAttempt} />
                  {latestRetry && (
                    <Accordion
                      elevation={0}
                      TransitionProps={{ unmountOnExit: true }}
                      style={{
                        border: "1px solid #dbe4f0",
                        borderLeft: "4px solid #3f51b5",
                        borderRadius: "4px",
                        backgroundColor: "#f5f8fc",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        id={`request-${modalData.id}-retries-toggle`}
                        aria-controls={`request-${modalData.id}-retries-content`}
                      >
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                          <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                            Reintentos ({retryAttempts.length})
                          </Typography>
                          <Chip
                            size="small"
                            label={`Último: HTTP ${latestRetry.status_code}`}
                            style={{
                              backgroundColor: latestRetry.status_code >= 400 ? "#ffebee" : "#e8f5e9",
                              color: latestRetry.status_code >= 400 ? "#b71c1c" : "#1b5e20",
                            }}
                          />
                        </div>
                      </AccordionSummary>
                      <AccordionDetails style={{ display: "block", padding: "8px 16px 0" }}>
                        {retryAttempts.map((attempt, index) => (
                          <AttemptResponse
                            key={index}
                            title={`Reintento ${index + 1}${index === retryAttempts.length - 1 ? " (último)" : ""}`}
                            attempt={attempt}
                          />
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Paper>
              </Grid>

            </Grid>
          ) : (
            <Typography>No se pudo cargar el detalle de la petición. Volvé a cargar los datos para continuar.</Typography>
          )}
          {hasListError && (
            <Typography color="error" style={{ marginTop: "16px" }}>
              No se pudo actualizar el listado. Volvé a cargar los datos antes de reintentar.
            </Typography>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px", justifyContent: "space-between" }}>
          <div>
            {modalData && modalData.http_response_status_code >= 400 && (
              <Button
                aria-label="Reintentar petición"
                onClick={handleRetryRequest}
                color="secondary"
                variant="contained"
                disabled={isRetrying || isModalLoading || isLoading || hasListError}
              >
                {isRetrying ? <CircularProgress size={24} color="inherit" /> : "Reintentar Petición"}
              </Button>
            )}
          </div>
          {(!modalData || hasListError) && !isRetrying && (
            <Button onClick={handleReloadData} color="primary" disabled={isModalLoading || isLoading}>
              Volver a cargar datos
            </Button>
          )}
          <Button onClick={handleCloseModal} color="primary" variant="contained" disabled={isRetrying}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
